/*
 * Quick-add modal, Amber Harlow layer. Works on top of Horizon's own quick add (assets/quick-add.js), which keeps
 * doing the real work: fetching the product, the variant picker, quantity, Add to cart and the cart drawer.
 * This file only adds, each time the modal content is drawn or redrawn:
 *  - desktop gallery: one image at a time with thumbnails (the theme's slides, nothing re-fetched)
 *  - the rating shown on the product card, when there is one
 *  - a note on what the chosen option is ("3 Lights" = one fixture, one piece toward the offer)
 *  - the multi-buy tiers with the shopper's real progress (cart.js + the quantity chosen here), and a shortcut that
 *    sets the quantity needed for the best tier
 *  - a "View full details" link
 * Discount tiers: data-tiers on the modal (snippets/hl-offer-tiers), matching the store's automatic discounts.
 * Styles: assets/hl-quick-add.css. Cart data: window.HLOffer (assets/hl-offer.js).
 */
import { StandardEvents } from '@shopify/events';

(function () {
  var dialog = document.getElementById('quick-add-dialog');
  var content = document.getElementById('quick-add-modal-content');
  if (!dialog || !content) return;
  var tiersRaw = dialog.dataset.tiers || '2:5,3:10';
  var mqDesktop = window.matchMedia('(min-width: 750px)');
  var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  var fmt = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currency });
  var lastCard = null;

  function offer() { return window.HLOffer; }
  function tiers() { return offer() ? offer().parseTiers(tiersRaw) : tiersRaw.split(',').map(function (t) { var a = t.split(':').map(Number); return { q: a[0], p: a[1] }; }); }
  function el(tag, cls, html) { var e = document.createElement(tag); if (cls) e.className = cls; if (html != null) e.innerHTML = html; return e; }
  function piece(n) { return n + (n === 1 ? ' piece' : ' pieces'); }

  // The card the shopper opened the modal from: rating and product id
  document.addEventListener('click', function (e) {
    var t = e.target instanceof Element ? e.target : null;
    var card = t && t.closest('product-card');
    if (card && t.closest('.quick-add__button, [data-hl-kc-cart]')) lastCard = card;
  }, true);

  /* ---------- gallery (desktop) ---------- */
  function gallery() {
    var media = content.querySelector('.product-information__media');
    if (!media) return;
    var slides = Array.prototype.slice.call(media.querySelectorAll('slideshow-slide'));
    if (!slides.length || media.querySelector('.hlqa-thumbs[data-for="' + slides.length + '-' + (slides[0].id || '') + '"]')) return;
    var old = media.querySelector('.hlqa-thumbs'); if (old) old.remove();
    function show(i) {
      slides.forEach(function (s, k) { s.classList.toggle('hlqa-on', k === i); });
      if (list) list.querySelectorAll('button').forEach(function (b, k) { b.setAttribute('aria-current', String(k === i)); });
    }
    var list = null;
    if (slides.length > 1) {
      list = el('ul', 'hlqa-thumbs'); list.dataset.for = slides.length + '-' + (slides[0].id || '');
      list.setAttribute('aria-label', 'Product images');
      slides.forEach(function (s, i) {
        var img = s.querySelector('img'); if (!img) return;
        var li = el('li'), b = el('button');
        b.type = 'button'; b.setAttribute('aria-label', 'Show image ' + (i + 1) + ' of ' + slides.length);
        var th = document.createElement('img');
        th.src = (img.currentSrc || img.src || '').replace(/([?&])width=\d+/, '$1width=160');
        th.alt = ''; th.loading = 'lazy'; th.width = 56; th.height = 56;
        b.appendChild(th); b.addEventListener('click', function () { show(i); });
        li.appendChild(b); list.appendChild(li);
      });
      (media.querySelector('media-gallery') || media).appendChild(list);
    }
    show(0);
  }

  /* ---------- rating from the card ---------- */
  function rating() {
    var host = content.querySelector('.view-product-title') || content.querySelector('.product-header');
    if (!host || content.querySelector('.hlqa-rating')) return;
    var src = lastCard && lastCard.querySelector('.hl-kc-stars[role="img"]');
    if (!src) return;
    var m = /Rated ([\d.]+) out of 5 from (\d+)/.exec(src.getAttribute('aria-label') || '');
    if (!m) return;
    var r = el('p', 'hlqa-rating');
    r.innerHTML = '<span class="hlqa-stars" style="--r:' + (parseFloat(m[1]) * 20) + '%" aria-hidden="true"></span><b>' + m[1] + '</b><span>(' + m[2] + ' reviews)</span>';
    r.setAttribute('aria-label', src.getAttribute('aria-label'));
    var titleLink = host.classList.contains('product-header') && host.querySelector(':scope > a');
    if (titleLink) titleLink.insertAdjacentElement('afterend', r); else host.insertAdjacentElement('afterend', r);
  }

  /* ---------- what the chosen option means ---------- */
  function lightsOf(value) { var m = /(\d+)\s*lights?\b/i.exec(value || ''); return m ? Number(m[1]) : 0; }
  function note() {
    var picker = content.querySelector('variant-picker');
    if (!picker) return;
    var values = Array.prototype.map.call(picker.querySelectorAll('input[type="radio"], option'), function (i) { return i.value; });
    var multi = values.some(function (v) { return lightsOf(v) > 1; });
    var n = content.querySelector('.hlqa-note');
    if (!multi) { if (n) n.remove(); return; }
    if (!n) { n = el('p', 'hlqa-note'); n.setAttribute('aria-live', 'polite'); picker.insertAdjacentElement('afterend', n); }
    var checked = picker.querySelector('input[type="radio"]:checked') || picker.querySelector('option:checked');
    var k = lightsOf(checked && checked.value);
    var icon = '<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9.2" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M12 11v5.5M12 7.6v.1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
    n.innerHTML = icon + '<span>' + (k > 1
      ? '<strong>' + checked.value + '</strong> is one fixture with ' + k + ' shades. Quantity is the number of fixtures; each one counts as one piece toward the offer.'
      : 'Each fixture counts as one piece toward the offer, whatever its number of lights.') + '</span>';
  }

  /* ---------- multi-buy ---------- */
  function qtyInput() { return content.querySelector('product-form-component input[name="quantity"], input[name="quantity"]'); }
  function qty() { var q = qtyInput(); return Math.max(1, parseInt(q && q.value, 10) || 1); }
  function unitPrice() {
    var p = content.querySelector('product-price .price bdi, product-price .price');
    var v = p ? parseFloat(p.textContent.replace(/[^0-9.]/g, '')) : NaN;
    return isFinite(v) ? v : null;
  }
  function addBlock() {
    var form = content.querySelector('.buy-buttons-block') || content.querySelector('product-form-component');
    if (!form) return null;
    var box = content.querySelector('.hlqa-offer');
    if (box) return box;
    var t = tiers(); if (!t.length) return null;
    var best = t[t.length - 1];
    box = el('div', 'hlqa-offer');
    box.setAttribute('role', 'group');
    box.setAttribute('aria-labelledby', 'hlqa-offer-title');
    var items = t.map(function (x, i) {
      var last = i === t.length - 1;
      return '<li class="hlqa-offer__tier' + (last ? ' hlqa-offer__tier--best' : '') + '" data-q="' + x.q + '">' + (last ? '<span class="hlqa-offer__flag">Best value</span>' : '') +
        '<span>' + x.q + (last ? '+' : '') + ' pieces</span><b>Save ' + x.p + '%</b></li>';
    }).join('');
    box.innerHTML = '<div class="hlqa-offer__head"><p class="hlqa-offer__title" id="hlqa-offer-title">' + (isIsland() ? 'Complete your island' : 'Mix &amp; match and save') + '</p><span class="hlqa-offer__auto">Applied automatically at checkout</span></div>' +
      '<ul class="hlqa-offer__tiers" role="list">' + items + '</ul>' +
      '<p class="hlqa-offer__status" aria-live="polite"></p>' +
      '<button type="button" class="hlqa-offer__set" hidden></button>';
    box.querySelector('.hlqa-offer__set').addEventListener('click', function () {
      var need = Number(this.dataset.qty), q = qtyInput();
      if (!q || !need) return;
      var sel = q.closest('quantity-selector-component');
      if (sel && sel.setValue) sel.setValue(String(need)); else q.value = String(need);
      q.dispatchEvent(new Event('input', { bubbles: true }));
      q.dispatchEvent(new Event('change', { bubbles: true }));
      if (sel && sel.updateButtonStates) try { sel.updateButtonStates(); } catch (e) {}
      update();
      q.focus({ preventScroll: true });
    });
    form.insertAdjacentElement('beforebegin', box);
    var input = qtyInput();
    if (input) input.setAttribute('aria-label', 'Quantity: number of fixtures');
    return box;
  }
  function isIsland() {
    var p = content.getAttribute('view-event-payload') || '';
    return /pendant|chandelier|ceiling|island/i.test(p) || /kitchen-island/.test(location.pathname);
  }
  function update() {
    var box = content.querySelector('.hlqa-offer'); if (!box) return;
    var O = offer(), cart = O && O.cart, t = tiers();
    var add = qty(), inCart = cart ? cart.count : 0, total = inCart + add;
    var now = null, next = null;
    t.forEach(function (x) { if (total >= x.q) now = x; else if (!next) next = x; });
    box.querySelectorAll('[data-q]').forEach(function (li) { li.classList.toggle('is-on', !!now && Number(li.dataset.q) === now.q); });
    var status = box.querySelector('.hlqa-offer__status'), set = box.querySelector('.hlqa-offer__set');
    var lead = cart ? (inCart ? 'With ' + piece(inCart) + ' already in your cart, adding ' + add + ' makes ' + piece(total) + '. ' : '') : '';
    var msg;
    if (now) {
      var unit = unitPrice();
      var est = cart && unit != null ? Math.floor((cart.subtotal + unit * 100 * add) * now.p / 100) / 100 : null;
      msg = lead + '<strong>' + now.p + '% off your order</strong>' + (est ? ' (about ' + fmt.format(est) + ')' : '') + ', applied at checkout.';
      if (next) msg += ' Add ' + (next.q - total) + ' more for ' + next.p + '%.';
    } else {
      msg = lead + (next ? 'Add ' + (next.q - total) + ' more piece' + (next.q - total > 1 ? 's' : '') + ' to save ' + next.p + '%' : '');
      var best = t[t.length - 1];
      if (best && best !== next) msg += ', or ' + (best.q - total) + ' more to save ' + best.p + '%';
      msg += '. Mix and match any pieces.';
    }
    status.innerHTML = cart || add > 1 ? msg : '';
    // Shortcut: the quantity that reaches the best tier, counting what is already in the cart
    var top = t[t.length - 1], need = top ? Math.max(1, top.q - inCart) : 0;
    var q = qtyInput();
    var max = q && q.max ? Number(q.max) : Infinity;
    var show = top && cart && total < top.q && need > add && need <= max && need <= 6;
    set.hidden = !show;
    if (show) { set.dataset.qty = need; set.textContent = 'Make it ' + need + ' and save ' + top.p + '%'; }
  }

  /* ---------- details link + reassurance ---------- */
  function foot() {
    var form = content.querySelector('.buy-buttons-block') || content.querySelector('product-form-component');
    if (!form) return;
    var a = content.querySelector('.view-product-title a, .product-header a');
    var f = content.querySelector('.hlqa-foot');
    if (!f) {
      f = el('div', 'hlqa-foot');
      f.innerHTML = '<a class="hlqa-details" href="#">View full details<svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></a>' +
        (dialog.dataset.trust ? '<p class="hlqa-trust">' + dialog.dataset.trust + '</p>' : '');
      form.insertAdjacentElement('afterend', f);
    }
    if (a) f.querySelector('.hlqa-details').href = a.href;
  }

  var busy = false;
  function enhance() {
    if (busy) return; busy = true;
    try {
      if (mqDesktop.matches) gallery();
      rating(); note(); addBlock(); foot(); update();
    } finally { busy = false; }
  }

  var raf = 0;
  new MutationObserver(function () { if (!raf) raf = requestAnimationFrame(function () { raf = 0; enhance(); }); })
    .observe(content, { childList: true, subtree: true });
  content.addEventListener('change', function (e) { if (e.target.matches && e.target.matches('input[name="quantity"], variant-picker *')) setTimeout(function () { note(); update(); }, 30); });
  content.addEventListener('input', function (e) { if (e.target.matches && e.target.matches('input[name="quantity"]')) update(); });
  content.addEventListener('click', function (e) { if (e.target.closest && e.target.closest('quantity-selector-component button, .quantity-selector button')) setTimeout(update, 30); });
  // Opening the modal: fresh cart numbers
  new MutationObserver(function () { var d = dialog.querySelector('dialog'); if (d && d.open && offer()) offer().load(); })
    .observe(dialog, { attributes: true, subtree: true, attributeFilter: ['open'] });
  // A successful add from the modal opens the cart drawer, like the cart buttons on the product cards do
  dialog.addEventListener(StandardEvents.cartLinesUpdate, function (e) {
    if (!e.promise || !e.promise.then) return;
    e.promise.then(function (r) {
      if (r && r.detail && r.detail.didError) return;
      var drawer = document.querySelector('cart-drawer-component');
      drawer = drawer && drawer.closest('theme-drawer');
      setTimeout(function () { if (drawer && !drawer.isOpen && drawer.open) drawer.open(); }, 350);
    }, function () {});
  });
  function hook() { if (offer()) offer().watch(update); else setTimeout(hook, 200); }
  hook();
})();
